import React, { useState, useEffect } from 'react';
import { DataService } from '../services/dataService';
import { MenuItem, GameItem, CalendarEvent } from '../types';
import { Trash2, Plus, ArrowLeft, Image as ImageIcon, Type, Tag, AlignLeft } from 'lucide-react';

interface AdminPageProps {
  onBack: () => void;
}

export const AdminPage: React.FC<AdminPageProps> = ({ onBack }) => {
  const [activeTab, setActiveTab] = useState<'menu' | 'games' | 'events'>('menu');
  const [menu, setMenu] = useState<MenuItem[]>([]);
  const [games, setGames] = useState<GameItem[]>([]);
  const [events, setEvents] = useState<CalendarEvent[]>([]);

  // Form states
  const [newItemName, setNewItemName] = useState('');
  const [newItemPrice, setNewItemPrice] = useState('');
  const [newItemDescription, setNewItemDescription] = useState('');
  const [newItemImage, setNewItemImage] = useState('');
  const [newItemCategory, setNewItemCategory] = useState('coffee');
  const [newItemGenre, setNewItemGenre] = useState('');
  
  // Refresh data
  const loadData = () => {
    setMenu(DataService.getMenu());
    setGames(DataService.getGames());
    setEvents(DataService.getEvents());
  };

  useEffect(() => {
    loadData();
  }, []);

  const resetForm = () => {
    setNewItemName('');
    setNewItemPrice('');
    setNewItemDescription('');
    setNewItemImage('');
    setNewItemCategory('coffee');
    setNewItemGenre('');
  };

  const handleAddMenu = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newItemName || !newItemPrice) return;
    DataService.addMenuItem({
      id: Date.now().toString(),
      name: newItemName,
      description: newItemDescription || 'Novinka v nabídce',
      price: Number(newItemPrice),
      category: newItemCategory as any,
      image: newItemImage || 'https://picsum.photos/400/300'
    });
    resetForm();
    loadData();
  };

  const handleAddGame = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newItemName || !newItemPrice) return;
    DataService.addGame({
      id: Date.now().toString(),
      title: newItemName,
      genre: newItemGenre || 'Action',
      price: Number(newItemPrice),
      rating: 5,
      image: newItemImage || 'https://picsum.photos/400/300'
    });
    resetForm();
    loadData();
  };

  const handleAddEvent = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newItemName) return;
    DataService.addEvent({
      id: Date.now().toString(),
      title: newItemName,
      date: new Date().toISOString(),
      description: newItemDescription || 'Přijďte se bavit!',
      type: 'tournament'
    });
    resetForm();
    loadData();
  };

  const handleDelete = (id: string, type: 'menu' | 'game' | 'event') => {
    if (confirm('Opravdu smazat?')) {
      if (type === 'menu') DataService.deleteMenuItem(id);
      if (type === 'game') DataService.deleteGame(id);
      if (type === 'event') DataService.deleteEvent(id);
      loadData();
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 p-4 md:p-8 font-sans">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
             <button onClick={onBack} className="p-2 bg-white rounded-full shadow hover:bg-gray-50 text-gray-600 transition-colors">
               <ArrowLeft />
             </button>
             <h1 className="text-2xl md:text-3xl font-bold text-gray-800">Administrace Obsahu</h1>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex space-x-1 md:space-x-4 mb-6 border-b border-gray-300 pb-1 overflow-x-auto">
          <button 
            onClick={() => setActiveTab('menu')}
            className={`pb-3 px-4 font-medium transition-colors whitespace-nowrap ${activeTab === 'menu' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
          >
            Kavárna Menu
          </button>
          <button 
            onClick={() => setActiveTab('games')}
            className={`pb-3 px-4 font-medium transition-colors whitespace-nowrap ${activeTab === 'games' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
          >
            Hry v Obchodě
          </button>
          <button 
            onClick={() => setActiveTab('events')}
            className={`pb-3 px-4 font-medium transition-colors whitespace-nowrap ${activeTab === 'events' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
          >
            Události
          </button>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
            {/* Form Column */}
            <div className="lg:col-span-1">
                <div className="bg-white rounded-xl shadow p-6 sticky top-8">
                    <h2 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                        <Plus className="w-5 h-5 text-blue-600" />
                        {activeTab === 'menu' ? 'Přidat položku' : activeTab === 'games' ? 'Přidat hru' : 'Přidat událost'}
                    </h2>
                    
                    <form onSubmit={activeTab === 'menu' ? handleAddMenu : activeTab === 'games' ? handleAddGame : handleAddEvent} className="space-y-4">
                        <div>
                            <label className="block text-xs font-bold text-gray-500 uppercase mb-1 flex items-center gap-1">
                                <Type size={14} /> Název
                            </label>
                            <input 
                                type="text" 
                                value={newItemName}
                                onChange={e => setNewItemName(e.target.value)}
                                placeholder="Zadejte název..."
                                className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 outline-none"
                                required
                            />
                        </div>

                        {/* Category / Genre Selector */}
                        {activeTab === 'menu' && (
                            <div>
                                <label className="block text-xs font-bold text-gray-500 uppercase mb-1 flex items-center gap-1">
                                    <Tag size={14} /> Kategorie
                                </label>
                                <select 
                                    value={newItemCategory}
                                    onChange={e => setNewItemCategory(e.target.value)}
                                    className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 outline-none"
                                >
                                    <option value="coffee">Káva</option>
                                    <option value="dessert">Dezerty</option>
                                    <option value="snack">Něco na zub</option>
                                    <option value="drink">Nápoje</option>
                                </select>
                            </div>
                        )}

                        {activeTab === 'games' && (
                            <div>
                                <label className="block text-xs font-bold text-gray-500 uppercase mb-1 flex items-center gap-1">
                                    <Tag size={14} /> Žánr
                                </label>
                                <input 
                                    type="text" 
                                    value={newItemGenre}
                                    onChange={e => setNewItemGenre(e.target.value)}
                                    placeholder="RPG, FPS, Racing..."
                                    className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 outline-none"
                                />
                            </div>
                        )}

                        {activeTab !== 'events' && (
                            <div>
                                <label className="block text-xs font-bold text-gray-500 uppercase mb-1 flex items-center gap-1">
                                    Cena (Kč)
                                </label>
                                <input 
                                    type="number" 
                                    value={newItemPrice}
                                    onChange={e => setNewItemPrice(e.target.value)}
                                    placeholder="0"
                                    className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 outline-none"
                                    required
                                />
                            </div>
                        )}

                        <div>
                            <label className="block text-xs font-bold text-gray-500 uppercase mb-1 flex items-center gap-1">
                                <AlignLeft size={14} /> Popis
                            </label>
                            <textarea 
                                value={newItemDescription}
                                onChange={e => setNewItemDescription(e.target.value)}
                                placeholder="Krátký popis..."
                                rows={3}
                                className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 outline-none resize-none"
                            />
                        </div>

                        {activeTab !== 'events' && (
                            <div>
                                <label className="block text-xs font-bold text-gray-500 uppercase mb-1 flex items-center gap-1">
                                    <ImageIcon size={14} /> URL Obrázku
                                </label>
                                <input 
                                    type="text" 
                                    value={newItemImage}
                                    onChange={e => setNewItemImage(e.target.value)}
                                    placeholder="https://..."
                                    className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 outline-none text-sm"
                                />
                                {newItemImage && (
                                    <div className="mt-2 h-20 rounded bg-gray-100 overflow-hidden relative">
                                        <img src={newItemImage} alt="Preview" className="w-full h-full object-cover opacity-80" onError={(e) => (e.currentTarget.style.display = 'none')} />
                                        <span className="absolute bottom-1 right-1 text-xs bg-black/50 text-white px-1 rounded">Náhled</span>
                                    </div>
                                )}
                            </div>
                        )}

                        <button type="submit" className="w-full py-2 bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center justify-center gap-2 font-medium transition-colors shadow-lg shadow-blue-600/20">
                            <Plus size={18} /> Přidat položku
                        </button>
                    </form>
                </div>
            </div>

            {/* List Column */}
            <div className="lg:col-span-2">
                <div className="bg-white rounded-xl shadow p-6">
                    <h2 className="text-lg font-bold text-gray-800 mb-4 border-b pb-2">Seznam položek</h2>
                    <div className="space-y-2">
                        {activeTab === 'menu' && menu.map(item => (
                        <div key={item.id} className="flex justify-between items-center p-3 border border-gray-100 rounded-lg hover:border-blue-200 hover:bg-blue-50 transition-all group bg-white">
                            <div className="flex items-center gap-3">
                                <div className="w-12 h-12 rounded bg-gray-200 overflow-hidden flex-shrink-0">
                                    {item.image ? <img src={item.image} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full bg-gray-300" />}
                                </div>
                                <div>
                                    <div className="font-bold text-gray-800">{item.name}</div>
                                    <div className="flex gap-2 text-xs">
                                        <span className="text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">{item.category}</span>
                                        <span className="text-gray-400 truncate max-w-[150px]">{item.description}</span>
                                    </div>
                                </div>
                            </div>
                            <div className="flex items-center gap-4">
                                <span className="font-mono font-bold text-gray-700">{item.price} Kč</span>
                                <button onClick={() => handleDelete(item.id, 'menu')} className="text-gray-400 hover:text-red-500 hover:bg-red-50 p-2 rounded transition-colors" title="Smazat"><Trash2 size={18} /></button>
                            </div>
                        </div>
                        ))}

                        {activeTab === 'games' && games.map(item => (
                        <div key={item.id} className="flex justify-between items-center p-3 border border-gray-100 rounded-lg hover:border-blue-200 hover:bg-blue-50 transition-all group bg-white">
                             <div className="flex items-center gap-3">
                                <div className="w-12 h-12 rounded bg-gray-200 overflow-hidden flex-shrink-0">
                                    {item.image ? <img src={item.image} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full bg-gray-300" />}
                                </div>
                                <div>
                                    <div className="font-bold text-gray-800">{item.title}</div>
                                    <div className="flex gap-2 text-xs">
                                        <span className="text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">{item.genre}</span>
                                    </div>
                                </div>
                            </div>
                            <div className="flex items-center gap-4">
                                <span className="font-mono font-bold text-gray-700">{item.price} Kč</span>
                                <button onClick={() => handleDelete(item.id, 'game')} className="text-gray-400 hover:text-red-500 hover:bg-red-50 p-2 rounded transition-colors" title="Smazat"><Trash2 size={18} /></button>
                            </div>
                        </div>
                        ))}

                        {activeTab === 'events' && events.map(item => (
                        <div key={item.id} className="flex justify-between items-center p-3 border border-gray-100 rounded-lg hover:border-blue-200 hover:bg-blue-50 transition-all group bg-white">
                            <div>
                                <span className="font-bold text-gray-800">{item.title}</span>
                                <p className="text-xs text-gray-500 flex gap-2 items-center mt-1">
                                    <span className="bg-blue-100 text-blue-700 px-1.5 rounded">{new Date(item.date).toLocaleDateString()}</span>
                                    <span className="truncate max-w-[200px]">{item.description}</span>
                                </p>
                            </div>
                            <button onClick={() => handleDelete(item.id, 'event')} className="text-gray-400 hover:text-red-500 hover:bg-red-50 p-2 rounded transition-colors" title="Smazat"><Trash2 size={18} /></button>
                        </div>
                        ))}
                        
                        {((activeTab === 'menu' && menu.length === 0) || (activeTab === 'games' && games.length === 0)) && (
                            <div className="text-center text-gray-400 py-12 flex flex-col items-center">
                                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-2">
                                    <Plus className="text-gray-300" size={32} />
                                </div>
                                <span>Zatím zde nic není. Přidejte první položku!</span>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
      </div>
    </div>
  );
};